/**
 * 자막 크기 단계 — 사용자 설정(+/−), 사용자별 저장.
 *
 * 크기는 **토큰 오버라이드**로 먹인다(§14 "발표 모드는 토큰 스케일
 * 오버라이드로"). 컴포넌트 크기를 직접 만지지 않고 루트에 --caption-*
 * 변수를 세우면 자막 타이포 전체가 따라온다.
 *
 * 값의 정본은 lib/caption-scale.ts 하나다 — 여기서는 그 값을 DOM 에
 * 옮겨 심을 뿐 숫자를 새로 적지 않는다.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  clampStep,
  DEFAULT_STEP,
  MAX_STEP,
  MIN_STEP,
  scaleAt,
  SCALE_STORAGE_KEY,
} from './caption-scale'

function read(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_STEP
  try {
    const raw = localStorage.getItem(SCALE_STORAGE_KEY)
    if (raw === null) return DEFAULT_STEP
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) ? DEFAULT_STEP : clampStep(n)
  } catch {
    return DEFAULT_STEP
  }
}

export function useCaptionScale(): {
  step: number
  setStep: (step: number) => void
  increase: () => void
  decrease: () => void
  canIncrease: boolean
  canDecrease: boolean
} {
  const [step, setStepState] = useState<number>(read)

  useEffect(() => {
    const scale = scaleAt(step)
    const root = document.documentElement
    root.style.setProperty('--caption-ko-size', `${scale.ko}rem`)
    root.style.setProperty('--caption-ko-leading', String(scale.leading))
    root.style.setProperty('--caption-src-size', `${scale.src}rem`)
    root.style.setProperty('--caption-pt-size', `${scale.passthrough}rem`)
    root.style.setProperty('--caption-meta-size', `${scale.meta}rem`)
    // 여백도 글자에서 파생한다 — 고정 클래스로 두면 확대·축소 어느 쪽에서든
    // 어긋난다(§14.1, 2026-08-06 실사용 요청).
    root.style.setProperty('--caption-pad-y', `${scale.padY}rem`)
    root.style.setProperty('--caption-pad-x', `${scale.padX}rem`)
    root.style.setProperty('--caption-gap', `${scale.gap}rem`)
    // 전사 카드는 같은 규칙을 배율만 달리해 적용한다 — 관계(항상 더 조밀)는
    // caption-scale.ts 의 산술이 보장하므로 여기서 다시 계산하지 않는다.
    root.style.setProperty('--caption-pt-pad-y', `${scale.ptPadY}rem`)
    root.style.setProperty('--caption-pt-pad-x', `${scale.ptPadX}rem`)
    root.style.setProperty('--caption-pt-gap', `${scale.ptGap}rem`)
    // 단계 자체도 남긴다 — e2e 가 "지금 몇 단계인가" 를 DOM 에서 읽는다.
    root.setAttribute('data-caption-step', String(scale.step))
    try {
      localStorage.setItem(SCALE_STORAGE_KEY, String(scale.step))
    } catch {
      // 프라이빗 모드 등 — 저장 실패는 무시한다(이번 세션만 유지).
    }
  }, [step])

  const setStep = useCallback((next: number) => setStepState(clampStep(next)), [])
  const increase = useCallback(() => setStepState((s) => clampStep(s + 1)), [])
  const decrease = useCallback(() => setStepState((s) => clampStep(s - 1)), [])

  return {
    step,
    setStep,
    increase,
    decrease,
    canIncrease: step < MAX_STEP,
    canDecrease: step > MIN_STEP,
  }
}
